<script setup>
import { useDialogManager } from '@0x234/common-vue/composables/dialog-manager';
import { useNotifyManager } from '@0x234/common-vue/composables/notify-manager';
import { useAuthStore } from 'stores/auth-store';
import { useJobShortlistStore } from 'stores/job-shortlist-store';
import { defineAsyncComponent, inject, ref } from 'vue';

const $props = defineProps({
    jobScope: {
        type: Object,
        required: true
    },
    inPreviewContext: {
        type: Boolean,
        default: false
    },
    noTooltip: {
        type: Boolean,
        default: false
    }
});

const $event = inject('$event');

const authStore = useAuthStore();
const jobShortlistStore = useJobShortlistStore();
const dialogManager = useDialogManager();
const notifyManager = useNotifyManager();

const isShortlistingInProgress = ref(false);
const isUserHoldingButton = ref(false);

async function createShortlistedJob() {
    if (!authStore.isUserAuthenticated) {
        openUserLoginDialog();
        return;
    }
    try {
        isShortlistingInProgress.value = true;
        await jobShortlistStore.createShortlistedJob($props.jobScope.id);
        $props.jobScope.shortlisted = true;
    } catch {
        notifyManager.notifyError('Could not add the job to your shortlist, please try again.');
    } finally {
        isShortlistingInProgress.value = false;
    }
}

async function deleteShortlistedJob() {
    if (!authStore.isUserAuthenticated) {
        openUserLoginDialog();
        return;
    }
    try {
        isShortlistingInProgress.value = true;
        await jobShortlistStore.deleteShortlistedJob($props.jobScope.id);
        $props.jobScope.shortlisted = false;
        $event.dispatchEvent(new CustomEvent('shortlist:deleted', { detail: { jobScopeId: $props.jobScope.id } }));
    } catch {
        notifyManager.notifyError('Could not remove the job from your shortlist, please try again.');
    } finally {
        isShortlistingInProgress.value = false;
    }
}

function onButtonClick() {
    if ($props.inPreviewContext) {
        return;
    }
    if (!authStore.isUserAuthenticated) {
        openUserLoginDialog();
        return;
    }
    if ($props.jobScope.shortlisted) {
        notifyManager.notifyInfo('Hold the button for a little longer to remove it from your shortlist.');
        return;
    }
    createShortlistedJob();
}

function onButtonHoldTimeout() {
    if ($props.inPreviewContext) {
        return;
    }
    if (!authStore.isUserAuthenticated) {
        openUserLoginDialog();
        return;
    }
    if ($props.jobScope.shortlisted) {
        deleteShortlistedJob();
    }
}

function onButtonStartHold() {
    if ($props.inPreviewContext) {
        return;
    }
    isUserHoldingButton.value = true;
}

function onButtonEndHold() {
    if ($props.inPreviewContext) {
        return;
    }
    isUserHoldingButton.value = false;
}

function openUserLoginDialog() {
    dialogManager.openDialogComponent(
        defineAsyncComponent(() => import('components/UserLoginDialog.vue')), {
            headline: 'Unlock your job search potential...',
            message:
                'Create or log in to your free account and start shortlisting jobs, tracking your applications, and ' +
                'unlocking access to our latest features!'
        });
}
</script>

<template>
    <q-btn
        @click.capture.stop="onButtonClick"
        v-touch-hold:1000.mouse="onButtonHoldTimeout"
        @touchstart="onButtonStartHold"
        @touchend="onButtonEndHold"
        @mousedown="onButtonStartHold"
        @mouseup="onButtonEndHold"
        @mouseleave="onButtonEndHold"
        :disable="isShortlistingInProgress"
        :color="jobScope.shortlisted ? (isUserHoldingButton ? 'negative' : 'yellow-8') : 'primary'"
        icon="sym_r_star_border"
        :class="{ 'material-symbols-rounded-fill': jobScope.shortlisted }"
        aria-label="Add job to shortlist"
        padding="6px"
        flat
    >
        <q-tooltip v-if="!noTooltip" :delay="1000">
            <template v-if="jobScope.shortlisted">Remove from shortlist</template>
            <template v-else>Add to shortlist</template>
        </q-tooltip>
    </q-btn>
</template>
